import React, { useState, useEffect, useCallback } from 'react';
import {
  X, CheckCircle, AlertTriangle, Clock, ExternalLink, Loader2, ChevronDown,
  ChevronRight, UserX, Edit, Plus, Save, Users, ArrowLeft, FileText, Trash2,
  MessageSquare, ClipboardList, Copy, Search, Link2, UserPlus,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { useAppTranslation } from '@/contexts/TranslationContext';
import type { OnboardingPlan, OnboardingPhase, PlanTask } from '../../../api/onboardingApi';
import * as api from '../../../api/onboardingApi';
import { apiClient } from '@/services/apiClient';
import NotesTimeline from './NotesTimeline';
import PlanDocumentsPanel from './PlanDocumentsPanel';
import ShareLinkManager from './ShareLinkManager';
import ContactsManager from './ContactsManager';

interface UserOption { id: number; name: string; email?: string; role?: string; }

const taskStatusConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  not_started: { label: 'Not Started', color: 'bg-gray-100 text-gray-700', icon: <Clock className="h-3 w-3" /> },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: <Loader2 className="h-3 w-3" /> },
  blocked: { label: 'Blocked', color: 'bg-red-100 text-red-700', icon: <AlertTriangle className="h-3 w-3" /> },
  waiting_customer: { label: 'Waiting', color: 'bg-yellow-100 text-yellow-700', icon: <UserX className="h-3 w-3" /> },
  done: { label: 'Done', color: 'bg-green-100 text-green-700', icon: <CheckCircle className="h-3 w-3" /> },
  skipped: { label: 'Skipped', color: 'bg-gray-100 text-gray-500', icon: <X className="h-3 w-3" /> },
};

const healthBadge: Record<string, string> = {
  green: 'bg-green-100 text-green-700', yellow: 'bg-yellow-100 text-yellow-700', red: 'bg-red-100 text-red-700',
};

// Page picker options for action links — matches sidebar navigation IDs
const PAGE_CATEGORIES = [
  { label: 'Org Setup', pages: [
    { id: 'cbo-management', label: 'Organizations' },
    { id: 'user-management', label: 'User Management' },
    { id: 'organization-setup-wizard', label: 'Setup Wizard' },
    { id: 'client-billing', label: 'Billing & Payments' },
    { id: 'compliance-documents', label: 'Compliance Documents' },
    { id: 'document-config', label: 'Document Management' },
  ]},
  { label: 'Access Control', pages: [
    { id: 'organization-roles', label: 'Organization Roles' },
    { id: 'saml-sso-config', label: 'SSO Configuration' },
    { id: 'data-privacy-settings', label: 'Data Privacy & Shield' },
    { id: 'external-systems', label: 'External Systems' },
  ]},
  { label: 'Programs & Workflows', pages: [
    { id: 'programs', label: 'Programs' },
    { id: 'contact-types', label: 'Contact Types & Pipelines' },
    { id: 'form-templates', label: 'Form Builder' },
    { id: 'workflows', label: 'Workflow Templates' },
    { id: 'program-billing', label: 'Program Billing' },
  ]},
  { label: 'Communications', pages: [
    { id: 'communication-templates', label: 'Communication Templates' },
    { id: 'campaign-templates', label: 'Campaign Templates' },
    // Phone Numbers reachable via Org Settings → Phone Numbers sub-tab.
  ]},
  { label: 'Data & Integration', pages: [
    { id: 'member-fields', label: 'Field Configuration' },
    { id: 'field-mapping', label: 'Field Mapping' },
    { id: 'integrations', label: 'Integration Workbench' },
    { id: 'config-migration', label: 'Configuration Migration' },
    { id: 'clearinghouse-settings', label: 'Clearinghouse Settings' },
    { id: 'hl7-config', label: 'HL7 Messaging' },
    { id: 'ihe-config', label: 'IHE Document Exchange' },
  ]},
  { label: 'Settings', pages: [
    { id: 'org-settings', label: 'Organization Settings' },
  ]},
];

const ALL_PAGES = PAGE_CATEGORIES.flatMap(c => c.pages);

// Map legacy /admin/xxx paths to sidebar tab IDs
const PATH_TO_TAB: Record<string, string> = {
  '/admin/programs': 'programs',
  '/admin/organization-roles': 'organization-roles',
  '/admin/member-fields': 'member-fields',
  '/admin/form-templates': 'form-templates',
  '/admin/compliance-documents': 'compliance-documents',
  '/admin/contact-types': 'contact-types',
  '/admin/communication-templates': 'communication-templates',
  '/admin/program-billing': 'program-billing',
  '/admin/field-mapping': 'field-mapping',
  '/admin/field-configuration': 'member-fields',
  '/admin/csv-upload': 'integrations',
  '/admin/saml-sso': 'saml-sso-config',
  '/admin/clearinghouse': 'clearinghouse-settings',
  '/admin/hl7-config': 'hl7-config',
  '/admin/ihe-config': 'ihe-config',
  '/admin/user-management': 'user-management',
  '/admin/organization-settings': 'org-settings',
  '/admin/organization-settings?tab=branding': 'org-settings',
  '/admin/organization-settings?tab=providers': 'org-settings',
  '/admin/organization-settings?tab=phones': 'org-settings',
  '/admin/organization-settings?tab=billing': 'org-settings',
  '/admin/organization-settings?tab=productivity': 'org-settings',
  '/admin/organization-setup?step=import-rules': 'organization-setup-wizard',
  '/admin/organization-setup?step=affiliations': 'organization-setup-wizard',
  '/admin/milestone-configuration': 'program-milestone-config',
  '/admin/employer-programs': 'employer-benefits-management',
  'OrganizationSetupWizard': 'organization-setup-wizard',
};

function resolveTabId(actionTarget: string): string {
  // Direct match to a known tab ID
  if (ALL_PAGES.find(p => p.id === actionTarget)) return actionTarget;
  // Legacy /admin/ path mapping
  if (PATH_TO_TAB[actionTarget]) return PATH_TO_TAB[actionTarget];
  // Strip /admin/ prefix and try
  const stripped = actionTarget.replace(/^\/admin\//, '');
  if (ALL_PAGES.find(p => p.id === stripped)) return stripped;
  return actionTarget;
}

function navigateToPage(actionTarget: string) {
  const tabId = resolveTabId(actionTarget);
  localStorage.setItem('activeTab', tabId);
  window.history.pushState({ tab: tabId }, '', window.location.href);
  window.dispatchEvent(new PopStateEvent('popstate', { state: { tab: tabId } }));
}

function getPageLabel(actionTarget: string): string {
  const tabId = resolveTabId(actionTarget);
  return ALL_PAGES.find(p => p.id === tabId)?.label || actionTarget.replace(/^\/admin\//, '').replace(/-/g, ' ');
}

const tabDefs = [
  { id: 'tasks' as const, label: 'Tasks', icon: <ClipboardList className="h-4 w-4" /> },
  { id: 'notes' as const, label: 'Notes', icon: <MessageSquare className="h-4 w-4" /> },
  { id: 'documents' as const, label: 'Documents', icon: <FileText className="h-4 w-4" /> },
  { id: 'sharing' as const, label: 'Sharing', icon: <Link2 className="h-4 w-4" /> },
  { id: 'contacts' as const, label: 'Contacts', icon: <UserPlus className="h-4 w-4" /> },
];
type TabType = typeof tabDefs[number]['id'];

interface Props {
  planId: number;
  onBack: () => void;
  onUpdated: () => void;
}

export default function PlanDetailPage({ planId, onBack, onUpdated }: Props) {
  const { t } = useAppTranslation();
  const [plan, setPlan] = useState<OnboardingPlan | null>(null);
  const [phases, setPhases] = useState<OnboardingPhase[]>([]);
  const [tasks, setTasks] = useState<PlanTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabType>('tasks');
  const [expandedPhases, setExpandedPhases] = useState<Set<number>>(new Set());
  const [users, setUsers] = useState<UserOption[]>([]);

  // Task editing
  const [editingTask, setEditingTask] = useState<PlanTask | null>(null);
  const [taskForm, setTaskForm] = useState({ name: '', description: '', status: '', owner_user_id: '', due_date: '', blocker_reason: '', notes: '', action_target: '' });

  // Add task
  const [showAddTask, setShowAddTask] = useState(false);
  const [addTaskPhaseId, setAddTaskPhaseId] = useState<number | null>(null);
  const [newTask, setNewTask] = useState({ name: '', description: '', owner_user_id: '', due_date: '', action_target: '' });
  const [addingTask, setAddingTask] = useState(false);

  // Follow-up
  const [showFollowup, setShowFollowup] = useState(false);
  const [followupParentId, setFollowupParentId] = useState<number | null>(null);
  const [followupForm, setFollowupForm] = useState({ name: '', due_date: '', owner_user_id: '', priority: 'medium' });

  // CSM editing
  const [editingCSM, setEditingCSM] = useState(false);
  const [csmUserId, setCsmUserId] = useState('');

  // Plan name editing
  const [editingName, setEditingName] = useState(false);
  const [planName, setPlanName] = useState('');

  // Save as template
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templatePublic, setTemplatePublic] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Page picker for action links
  const [pagePickerSearch, setPagePickerSearch] = useState('');

  const fetchPlan = useCallback(async () => {
    try {
      const [data, usersRes] = await Promise.all([
        api.getPlan(planId),
        apiClient.get<UserOption[]>('/api/users/all-staff').catch(() => ({ data: [] })),
      ]);
      setPlan(data.plan);
      setPhases(data.phases);
      setTasks(data.tasks);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data as any)?.users || []);
      if (data.plan.current_phase_id) {
        setExpandedPhases(prev => prev.size > 0 ? prev : new Set([data.plan.current_phase_id!]));
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [planId]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  const togglePhase = (phaseId: number) => {
    setExpandedPhases(prev => {
      const next = new Set(prev);
      next.has(phaseId) ? next.delete(phaseId) : next.add(phaseId);
      return next;
    });
  };

  const handleStatusChange = async (task: PlanTask, newStatus: string) => {
    if (newStatus === 'blocked') { openTaskEditor(task, 'blocked'); return; }
    try { await api.updatePlanTask(planId, task.id, { status: newStatus }); fetchPlan(); onUpdated(); }
    catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  const openTaskEditor = (task: PlanTask, forceStatus?: string) => {
    setEditingTask(task);
    setTaskForm({
      name: task.name || '', description: task.description || '',
      status: forceStatus || task.status,
      owner_user_id: task.owner_user_id ? String(task.owner_user_id) : '',
      due_date: task.due_date || '', blocker_reason: task.blocker_reason || '',
      notes: task.notes || '', action_target: task.action_target || '',
    });
    setPagePickerSearch('');
  };

  const handleSaveTaskEdit = async () => {
    if (!editingTask) return;
    const updates: Record<string, any> = {};
    if (taskForm.name !== editingTask.name) updates.name = taskForm.name;
    if (taskForm.description !== (editingTask.description || '')) updates.description = taskForm.description || null;
    if (taskForm.status !== editingTask.status) updates.status = taskForm.status;
    const ownerId = taskForm.owner_user_id ? parseInt(taskForm.owner_user_id) : null;
    if (ownerId !== editingTask.owner_user_id) updates.owner_user_id = ownerId;
    if (taskForm.due_date !== (editingTask.due_date || '')) updates.due_date = taskForm.due_date || null;
    if (taskForm.blocker_reason !== (editingTask.blocker_reason || '')) updates.blocker_reason = taskForm.blocker_reason || null;
    if (taskForm.notes !== (editingTask.notes || '')) updates.notes = taskForm.notes || null;
    if (taskForm.action_target !== (editingTask.action_target || '')) updates.action_target = taskForm.action_target || null;
    if (Object.keys(updates).length === 0) { setEditingTask(null); return; }
    try { await api.updatePlanTask(planId, editingTask.id, updates); setEditingTask(null); fetchPlan(); onUpdated(); }
    catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!confirm('Delete this task from the plan?')) return;
    try { await apiClient.delete(`/api/platform/v1/support/onboarding/plans/${planId}/tasks/${taskId}`); fetchPlan(); onUpdated(); }
    catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  const openAddTask = (phaseId: number) => {
    setAddTaskPhaseId(phaseId);
    setNewTask({ name: '', description: '', owner_user_id: '', due_date: '', action_target: '' });
    setShowAddTask(true);
    setPagePickerSearch('');
  };

  const handleAddTask = async () => {
    if (!newTask.name.trim() || !addTaskPhaseId) return;
    setAddingTask(true);
    try {
      await apiClient.post(`/api/platform/v1/support/onboarding/plans/${planId}/tasks`, {
        phase_id: addTaskPhaseId, name: newTask.name, description: newTask.description || null,
        owner_user_id: newTask.owner_user_id ? parseInt(newTask.owner_user_id) : null,
        due_date: newTask.due_date || null, action_target: newTask.action_target || null,
      });
      setShowAddTask(false); fetchPlan(); onUpdated();
    } catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
    finally { setAddingTask(false); }
  };

  const openFollowup = (parentTaskId: number) => {
    setFollowupParentId(parentTaskId);
    setFollowupForm({ name: '', due_date: '', owner_user_id: '', priority: 'medium' });
    setShowFollowup(true);
  };

  const handleCreateFollowup = async () => {
    if (!followupForm.name.trim() || !followupParentId) return;
    try {
      await api.createFollowup(planId, followupParentId, {
        name: followupForm.name.trim(), due_date: followupForm.due_date || undefined,
        owner_user_id: followupForm.owner_user_id ? parseInt(followupForm.owner_user_id) : undefined,
        priority: followupForm.priority,
      });
      setShowFollowup(false); fetchPlan(); onUpdated();
    } catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  const savePlanName = async () => {
    if (!planName.trim()) { setEditingName(false); return; }
    try { await api.updatePlan(planId, { name: planName.trim() }); setEditingName(false); fetchPlan(); onUpdated(); }
    catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  const saveCSM = async () => {
    try { await api.updatePlan(planId, { csm_user_id: csmUserId ? parseInt(csmUserId) : undefined }); setEditingCSM(false); fetchPlan(); onUpdated(); }
    catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  // Save plan as template
  const handleSaveAsTemplate = async () => {
    if (!templateName.trim() || !plan) return;
    setSavingTemplate(true);
    try {
      await apiClient.post('/api/platform/v1/support/onboarding/plans/' + planId + '/save-as-template', {
        name: templateName.trim(),
        make_public: templatePublic,
      });
      setShowSaveTemplate(false);
      alert('Template created successfully!');
    } catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
    finally { setSavingTemplate(false); }
  };

  // Page picker helper
  const filteredPages = pagePickerSearch
    ? ALL_PAGES.filter(p => p.label.toLowerCase().includes(pagePickerSearch.toLowerCase()) || p.id.includes(pagePickerSearch.toLowerCase()))
    : ALL_PAGES;

  const renderPagePicker = (value: string, onChange: (v: string) => void) => (
    <div>
      <Label>Action Link (page to open)</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1"><SelectValue placeholder="Link to a page..." /></SelectTrigger>
        <SelectContent>
          <div className="px-2 pb-1.5">
            <Input placeholder="Search pages..." value={pagePickerSearch} onChange={(e) => { e.stopPropagation(); setPagePickerSearch(e.target.value); }} className="h-7 text-xs" />
          </div>
          <SelectItem value="">None</SelectItem>
          {PAGE_CATEGORIES.map(cat => {
            const catPages = cat.pages.filter(p => filteredPages.includes(p));
            if (catPages.length === 0) return null;
            return (
              <React.Fragment key={cat.label}>
                <div className="px-2 py-1 text-xs font-medium text-gray-400">{cat.label}</div>
                {catPages.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </React.Fragment>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!plan) return <div className="text-center py-16 text-gray-400">Plan not found. <Button variant="link" onClick={onBack}>Go back</Button></div>;

  const taskTotal = tasks.length;
  const taskDone = tasks.filter(t => t.status === 'done' || t.status === 'skipped').length;
  const pctComplete = taskTotal > 0 ? Math.round((taskDone / taskTotal) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-1" />Back to Plans</Button>
        <div className="flex-1">
          {editingName ? (
            <div className="flex items-center gap-2">
              <Input className="h-9 text-lg font-semibold w-80" value={planName} onChange={(e) => setPlanName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && savePlanName()} autoFocus />
              <Button size="sm" variant="ghost" onClick={savePlanName}><Save className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingName(false)}><X className="h-4 w-4" /></Button>
            </div>
          ) : (
            <h1 className="text-xl font-semibold cursor-pointer hover:text-blue-600" onClick={() => { setPlanName(plan.name || plan.customer_org_name || ''); setEditingName(true); }}>
              {plan.name || plan.customer_org_name || `Plan #${plan.id}`}
              <Edit className="h-3.5 w-3.5 inline ml-2 opacity-40" />
            </h1>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => { setTemplateName((plan.name || plan.customer_org_name || '') + ' Template'); setTemplatePublic(false); setShowSaveTemplate(true); }}>
          <Copy className="h-3.5 w-3.5 mr-1" />Save as Template
        </Button>
        <Badge variant="outline" className={healthBadge[plan.health] || ''}>{plan.health}</Badge>
        <Badge variant="secondary">{plan.status}</Badge>
      </div>

      {/* Progress cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card><CardContent className="py-3 text-center">
          <p className="text-xs text-gray-400">Progress</p>
          <p className="text-xl font-bold">{pctComplete}%</p>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
            <div className={`h-1.5 rounded-full ${pctComplete === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pctComplete}%` }} />
          </div>
        </CardContent></Card>
        <Card><CardContent className="py-3 text-center"><p className="text-xs text-gray-400">Phase</p><p className="font-medium text-sm mt-1">{plan.current_phase_name || '—'}</p></CardContent></Card>
        <Card className="cursor-pointer hover:bg-gray-50" onClick={() => { setCsmUserId(plan.csm_user_id ? String(plan.csm_user_id) : ''); setEditingCSM(true); }}>
          <CardContent className="py-3 text-center"><p className="text-xs text-gray-400">CSM <Edit className="h-2.5 w-2.5 inline opacity-40" /></p><p className="font-medium text-sm mt-1">{plan.csm_user_name || <span className="text-gray-400">Assign</span>}</p></CardContent>
        </Card>
        <Card><CardContent className="py-3 text-center"><p className="text-xs text-gray-400">Go-Live Target</p><p className="font-medium text-sm mt-1">{plan.target_go_live_date ? new Date(plan.target_go_live_date).toLocaleDateString() : '—'}</p></CardContent></Card>
        <Card><CardContent className="py-3 text-center"><p className="text-xs text-gray-400">Started</p><p className="font-medium text-sm mt-1">{plan.start_date ? new Date(plan.start_date).toLocaleDateString() : '—'}</p></CardContent></Card>
        <Card><CardContent className="py-3 text-center"><p className="text-xs text-gray-400">Tasks</p><p className="font-medium text-sm mt-1">{taskDone}/{taskTotal} done</p></CardContent></Card>
      </div>

      {/* Tabs */}
      <div className="flex border-b">
        {tabDefs.map(td => (
          <button key={td.id} onClick={() => setTab(td.id)} className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === td.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {td.icon}{td.label}
          </button>
        ))}
      </div>

      {/* Tasks tab */}
      {tab === 'tasks' && (
        <div className="space-y-3">
          {phases.map((phase) => {
            const phaseTasks = tasks.filter(t => t.phase_id === phase.id);
            const phaseDone = phaseTasks.filter(t => t.status === 'done' || t.status === 'skipped').length;
            const isExpanded = expandedPhases.has(phase.id);
            const isCurrent = plan.current_phase_id === phase.id;
            return (
              <Card key={phase.id} className={isCurrent ? 'border-blue-300 bg-blue-50/30' : ''}>
                <div className="flex items-center justify-between px-4 py-3">
                  <button onClick={() => togglePhase(phase.id)} className="flex items-center gap-2 flex-1">
                    {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                    <span className="font-medium">{phase.name}</span>
                    {isCurrent && <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700">Current</Badge>}
                    <span className="text-xs text-gray-500 ml-auto">{phaseDone}/{phaseTasks.length}</span>
                  </button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 ml-2" onClick={() => openAddTask(phase.id)}><Plus className="h-3 w-3 mr-1" />Add Task</Button>
                </div>
                {isExpanded && (
                  <CardContent className="pt-0 pb-3">
                    {/* Task table header */}
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-gray-400 font-medium border-b">
                          <th className="text-left py-1.5 px-2 w-28">Status</th>
                          <th className="text-left py-1.5 px-2">Task</th>
                          <th className="text-left py-1.5 px-2 w-28">Owner</th>
                          <th className="text-left py-1.5 px-2 w-24">Due</th>
                          <th className="text-left py-1.5 px-2 w-36">Action</th>
                          <th className="text-right py-1.5 px-2 w-28"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                      {phaseTasks.map((task) => {
                        const cfg = taskStatusConfig[task.status] || taskStatusConfig.not_started;
                        return (
                          <tr key={task.id} className="group hover:bg-gray-50">
                            {/* Status */}
                            <td className="py-2 px-2">
                              <Select value={task.status} onValueChange={(v) => handleStatusChange(task, v)}>
                                <SelectTrigger className={`h-7 text-xs rounded-full px-2.5 w-fit ${cfg.color}`}>
                                  <SelectValue>{cfg.icon}<span className="ml-1">{cfg.label}</span></SelectValue>
                                </SelectTrigger>
                                <SelectContent>{Object.entries(taskStatusConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </td>
                            {/* Task name */}
                            <td className="py-2 px-2 cursor-pointer" onClick={() => openTaskEditor(task)}>
                              <p className={`text-sm hover:text-blue-600 ${task.status === 'done' ? 'line-through text-gray-400' : ''}`}>
                                {(task as any).is_followup && <Badge variant="outline" className="text-xs mr-1 bg-amber-50 text-amber-700">Follow-up</Badge>}
                                {task.name}
                              </p>
                              {task.blocker_reason && <p className="text-xs text-red-500 mt-0.5">Blocked: {task.blocker_reason}</p>}
                            </td>
                            {/* Owner */}
                            <td className="py-2 px-2 text-xs text-gray-600 truncate max-w-[7rem]">
                              {task.owner_name || <span className="text-gray-300">—</span>}
                            </td>
                            {/* Due */}
                            <td className="py-2 px-2">
                              {task.due_date ? (
                                <span className={`text-xs whitespace-nowrap ${new Date(task.due_date) < new Date() && task.status !== 'done' && task.status !== 'skipped' ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                  {new Date(task.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                </span>
                              ) : <span className="text-xs text-gray-300">—</span>}
                            </td>
                            {/* Action link */}
                            <td className="py-2 px-2">
                              {task.action_target ? (
                                <button className="text-xs text-blue-600 hover:text-blue-800 hover:underline truncate block max-w-[9rem]" onClick={(e) => { e.stopPropagation(); navigateToPage(task.action_target!); }} title={`Go to: ${getPageLabel(task.action_target)}`}>
                                  {getPageLabel(task.action_target)}
                                </button>
                              ) : <span className="text-xs text-gray-300">—</span>}
                            </td>
                            {/* Actions */}
                            <td className="py-2 px-2 text-right whitespace-nowrap">
                              <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => openTaskEditor(task)}>Edit</Button>
                              <Button size="sm" variant="ghost" className="h-6 px-1 ml-0.5" onClick={() => openFollowup(task.id)} title="Follow-up"><Plus className="h-3 w-3" /></Button>
                              <Button size="sm" variant="ghost" className="h-6 px-1 text-gray-300 hover:text-red-500" onClick={() => handleDeleteTask(task.id)} title="Delete"><Trash2 className="h-3 w-3" /></Button>
                            </td>
                          </tr>
                        );
                      })}
                      {phaseTasks.length === 0 && (
                        <tr><td colSpan={6} className="py-4 text-sm text-gray-400 text-center">No tasks. <button className="text-blue-500 hover:underline" onClick={() => openAddTask(phase.id)}>Add one</button></td></tr>
                      )}
                      </tbody>
                    </table>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {tab === 'notes' && <NotesTimeline planId={planId} />}
      {tab === 'documents' && <PlanDocumentsPanel planId={planId} tasks={tasks} />}
      {tab === 'sharing' && <ShareLinkManager planId={planId} tasks={tasks} />}
      {tab === 'contacts' && <ContactsManager planId={planId} />}

      {/* ===== Task editor dialog ===== */}
      {editingTask && (
        <Dialog open onOpenChange={() => setEditingTask(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Edit Task</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
              <div><Label>Task Name</Label><Input value={taskForm.name} onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })} className="mt-1" /></div>
              <div><Label>Description</Label><Textarea rows={2} value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Status</Label>
                  <Select value={taskForm.status} onValueChange={(v) => setTaskForm({ ...taskForm, status: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(taskStatusConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Owner</Label>
                  <Select value={taskForm.owner_user_id} onValueChange={(v) => setTaskForm({ ...taskForm, owner_user_id: v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Assign..." /></SelectTrigger>
                    <SelectContent><SelectItem value="">Unassigned</SelectItem>{users.map(u => <SelectItem key={u.id} value={String(u.id)}>{u.name || u.email}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Due Date</Label><Input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} className="mt-1" /></div>
              {taskForm.status === 'blocked' && <div><Label>Blocker Reason</Label><Input value={taskForm.blocker_reason} onChange={(e) => setTaskForm({ ...taskForm, blocker_reason: e.target.value })} className="mt-1" /></div>}
              {renderPagePicker(taskForm.action_target, (v) => setTaskForm({ ...taskForm, action_target: v }))}
              <div><Label>Notes</Label><Textarea rows={2} value={taskForm.notes} onChange={(e) => setTaskForm({ ...taskForm, notes: e.target.value })} className="mt-1" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" className="text-red-500 mr-auto" onClick={() => { setEditingTask(null); handleDeleteTask(editingTask.id); }}><Trash2 className="h-4 w-4 mr-1" />Delete</Button>
              <Button variant="outline" onClick={() => setEditingTask(null)}>Cancel</Button>
              <Button onClick={handleSaveTaskEdit}><Save className="h-4 w-4 mr-1" />Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ===== Add task dialog ===== */}
      {showAddTask && (
        <Dialog open onOpenChange={() => setShowAddTask(false)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Task</DialogTitle><DialogDescription>Add a task to this plan phase.</DialogDescription></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Task Name</Label><Input value={newTask.name} onChange={(e) => setNewTask({ ...newTask, name: e.target.value })} className="mt-1" autoFocus /></div>
              <div><Label>Description</Label><Textarea rows={2} value={newTask.description} onChange={(e) => setNewTask({ ...newTask, description: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Owner</Label>
                  <Select value={newTask.owner_user_id} onValueChange={(v) => setNewTask({ ...newTask, owner_user_id: v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Assign..." /></SelectTrigger>
                    <SelectContent><SelectItem value="">Unassigned</SelectItem>{users.map(u => <SelectItem key={u.id} value={String(u.id)}>{u.name || u.email}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Due Date</Label><Input type="date" value={newTask.due_date} onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })} className="mt-1" /></div>
              </div>
              {renderPagePicker(newTask.action_target, (v) => setNewTask({ ...newTask, action_target: v }))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddTask(false)}>Cancel</Button>
              <Button onClick={handleAddTask} disabled={addingTask || !newTask.name.trim()}>{addingTask ? 'Adding...' : 'Add Task'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ===== Follow-up dialog ===== */}
      {showFollowup && (
        <Dialog open onOpenChange={() => setShowFollowup(false)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Follow-up</DialogTitle><DialogDescription>Action item linked to the parent task.</DialogDescription></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Name</Label><Input value={followupForm.name} onChange={(e) => setFollowupForm({ ...followupForm, name: e.target.value })} className="mt-1" autoFocus /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Priority</Label>
                  <Select value={followupForm.priority} onValueChange={(v) => setFollowupForm({ ...followupForm, priority: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="urgent">Urgent</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Due Date</Label><Input type="date" value={followupForm.due_date} onChange={(e) => setFollowupForm({ ...followupForm, due_date: e.target.value })} className="mt-1" /></div>
              </div>
              <div>
                <Label>Owner</Label>
                <Select value={followupForm.owner_user_id} onValueChange={(v) => setFollowupForm({ ...followupForm, owner_user_id: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Assign..." /></SelectTrigger>
                  <SelectContent><SelectItem value="">Unassigned</SelectItem>{users.map(u => <SelectItem key={u.id} value={String(u.id)}>{u.name || u.email}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFollowup(false)}>Cancel</Button>
              <Button onClick={handleCreateFollowup} disabled={!followupForm.name.trim()}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ===== CSM dialog ===== */}
      {editingCSM && (
        <Dialog open onOpenChange={() => setEditingCSM(false)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Assign CSM</DialogTitle><DialogDescription>Select a Customer Success Manager.</DialogDescription></DialogHeader>
            <div className="py-2">
              <Select value={csmUserId} onValueChange={setCsmUserId}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent><SelectItem value="">Unassigned</SelectItem>{users.map(u => <SelectItem key={u.id} value={String(u.id)}>{u.name || u.email}{u.role ? ` (${u.role})` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setEditingCSM(false)}>Cancel</Button><Button onClick={saveCSM}>Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ===== Save as Template dialog ===== */}
      {showSaveTemplate && (
        <Dialog open onOpenChange={() => setShowSaveTemplate(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save Plan as Template</DialogTitle>
              <DialogDescription>Create a reusable template from this plan's phases and tasks.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Template Name</Label><Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} className="mt-1" autoFocus /></div>
              <div className="flex items-start gap-3 p-3 border rounded-lg">
                <input type="checkbox" checked={templatePublic} onChange={(e) => setTemplatePublic(e.target.checked)} className="mt-1" id="make-public" />
                <div>
                  <label htmlFor="make-public" className="text-sm font-medium cursor-pointer">Share as platform template</label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    When enabled, this template will be visible to all organizations on the platform as a starter template they can clone.
                    Your organization's specific data (names, dates, owners) will not be included — only the phase/milestone/task structure.
                  </p>
                  {templatePublic && (
                    <p className="text-xs text-amber-600 mt-1 bg-amber-50 p-1.5 rounded">
                      By making this public, you confirm that the template structure does not contain confidential or proprietary information specific to your organization or clients.
                    </p>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSaveTemplate(false)}>Cancel</Button>
              <Button onClick={handleSaveAsTemplate} disabled={savingTemplate || !templateName.trim()}>
                {savingTemplate ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Saving...</> : <><Copy className="h-4 w-4 mr-1" />Create Template</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
